import { Active, DataRef, Over } from "@dnd-kit/core";
import { ColumnDragData } from "views/admin/project/launched_project/components/BoardColumn";
import { TaskDragData } from "views/admin/project/launched_project/components/TaskCard";

enum TypeMask {
    phone = "phone",
    cnpj = "cnpj",
    cpf = "cpf",
    cep = "cep",
    rg = "rg",
    ctps = "ctps",
    pispasep = "pispasep",
}

type convertPatternType = {
    [key in TypeMask]: {
        search: RegExp;
        length: number;
        replace: string;
    }
}

const convertPattern: convertPatternType = {
    phone: {
        search: /(\d{2})(\d{5})(\d{4})/,
        length: 11,
        replace: '($1) $2-$3'
    },
    cnpj: {
        search: /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        length: 14,
        replace: '$1.$2.$3/$4-$5'
    },
    cpf: {
        search: /(\d{3})(\d{3})(\d{3})(\d{2})/,
        length: 11,
        replace: '$1.$2.$3-$4'
    },
    cep: {
        search: /(\d{5})(\d{3})/,
        length: 8,
        replace: '$1-$2'
    },
    rg: {
        search: /(\d{2})(\d{3})(\d{3})(\d{1})/,
        length: 9,
        replace: '$1.$2.$3-$4'
    },
    ctps: {
        search: /(\d{7})(\d{4})/,
        length: 11,
        replace: '$1/$2'
    },
    pispasep: {
        search: /(\d{3})(\d{5})(\d{2})(\d{1})/,
        length: 11,
        replace: '$1.$2.$3-$4'
    },
};


/**
 *  Convert masks format
 *  @description - Convert the value to the desired mask or remove the mask
 *
 *  @param type - Type of mask
 *  @param input - Value to convert
 *  @returns - Return the value converted
 *  @example - convertMasksFormat("phone", "17999999977") // Return "(17) 99999-9977"
 *  @example - convertMasksFormat("phone", "(17) 99999-9977") // Return "17999999977"
 * */
export function convertMasksFormat(type: keyof typeof TypeMask, input: string) {
    const { length, search, replace } = convertPattern[type];

    if (new RegExp(`^\\d{${length}}$`).test(input)) {
        // exm: Convert "17999999977" to "(17) 99999-9977"
        return input.replace(search, replace);
    } else {
        // exm: Convert "(17) 99999-9977" to "17999999977"
        return input.replace(/\D/g, '');
    }
}

// =====================================================================================================================

enum TypeValidators {
    email = "email",
    cnpj = "cnpj",
    cpf = "cpf",
    ctps = "ctps",
    rg = "rg",
    phone = "phone",
    cep = "cep",
    pispasep = "pispasep",
    password = "password",
    minLength = "minLength",
    required = "required",
}

const regexPatterns: { [K in keyof typeof TypeValidators]: RegExp } = {
    email: /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    cnpj: /^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/,  // 00.000.000/0000-00 ou 00000000000000
    cpf: /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/,           // 000.000.000-00 ou 00000000000
    pispasep: /^\d{3}\.?\d{5}\.?\d{2}-?\d{1}$/,      // 000.00000.00-0 ou 00000000000
    ctps: /^\d{7}\/?\d{4}$/,                         // 0000000/0000 ou 00000000000
    rg: /^\d{2}\.?\d{3}\.?\d{3}-?\d{1}$/,           // 00.000.000-0 ou 000000000
    phone: /^\(?\d{2}\)?\s?\d{4,5}-?\d{4}$/,        // (99) 99999-9999 ou 99999999999
    cep: /^\d{5}-?\d{3}$/,                          // 00000-000 ou 00000000
    password: /^(?=.*\d)(?=.*[a-z]).{8,}$/,         // Senha com pelo menos um número e uma letra
    minLength: /^.{4,}$/,                           // Mínimo de 4 caracteres
    required: /^.{1,}$/,                            // Campo obrigatório
};

/**
 *  Validator String
 *  @description - Validate the string with the type and value for build
 *
 *  @param type - Type of TypeValidators
 *  @param value - Value to validate
 *  @param min - Minimum length for the value
 *  @returns - Return the validation result true or false
 *  @example -
 *          const validations: validatorEngine.type<typeof TYPE_FORM> = {
 *             KEY: {
 *                 test: () => validatorEngine.testString("minLength", user.nome, 8),
 *                 errorMessage: "O nome precisa ter mais de 8 caracteres"
 *             },
 *             ...
 *         };
 *
 *  @example -
 *      const {isValid, errors} = validatorEngine.build(validations);
 * */
const validatorString = (type: keyof typeof TypeValidators, value: string, min?: number): boolean => {
    if (type === TypeValidators.minLength && min) {
        return !(value.length < min)
    }

    return regexPatterns[type].test(value)
}

type typeOfValidationObjectInputs<T> = {
    [K in keyof T]: { test: () => boolean; errorMessage: string };
}

type typeEngine<T> = (object: typeOfValidationObjectInputs<T>) => {
    isValid: boolean;
    errors: { [K in keyof T]: string };
};


/**
 *  Validator Build
 *  @description - Validate the object with the keys and return the result
 *
 *  @param object - Object with the keys to validate
 *  @returns - Return {isValid, errors} with the validation result
 *  @example -
 *          const validations: validatorEngine.type<typeof TYPE_FORM> = {
 *             KEY: {
 *                 test: () => validatorEngine.testString("minLength", user.nome, 8),
 *                 errorMessage: "O nome precisa ter mais de 8 caracteres"
 *             },
 *             ...
 *         };
 *
 *  @example -
 *      const {isValid, errors} = validatorEngine.build(validations);
 * */
const validatorBuild = <T>(object: typeOfValidationObjectInputs<T>): ReturnType<typeEngine<T>> => {

    let errors = {} as ReturnType<typeEngine<T>>["errors"];
    let isValid = true;

    for (const key in object) {
        const element = object[key as keyof typeof object];
        const res = element.test();

        errors[key as keyof T] = res ? "" : element.errorMessage;

        if (!res) {
            isValid = false;
        }
    }

    return {
        isValid,
        errors
    };
};

export const validatorEngine = {
    testString: validatorString,
    build: validatorBuild
}

export namespace validatorEngine {
    export type type<T> = typeOfValidationObjectInputs<T>;
}

// =====================================================================================================================

type DraggableData = ColumnDragData | TaskDragData;

export function hasDraggableData<T extends Active | Over>(
    entry: T | null | undefined
): entry is T & {
    data: DataRef<DraggableData>;
} {
    if (!entry) {
        return false;
    }

    const data = entry.data.current;

    if (data?.type === "Column" || data?.type === "Task") {
        return true;
    }

    return false;
}

export function generateId() {
    return Math.random().toString(36).substr(2, 9);
}